import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * Google OAuth callback. Handles two flows:
 *  1. Native Google OAuth (Phase 12.4B): url is /auth/google/callback?code=…&state=…
 *  2. Legacy Emergent proxy: url contains #session_id=…
 * Both are detected in App.js and route here.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { exchangeGoogleSession, checkAuth } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    (async () => {
      try {
        // -- Native Google OAuth: ?code=&state= -----------------------------
        const search = new URLSearchParams(window.location.search);
        const code = search.get("code");
        const state = search.get("state");
        const oauthError = search.get("error");
        if (oauthError) {
          throw new Error(`google_error:${oauthError}`);
        }
        if (code && state) {
          const redirect_uri = window.location.origin + "/auth/google/callback";
          const { data } = await api.post("/auth/google/exchange", { code, state, redirect_uri });
          if (data?.session_token) {
            localStorage.setItem("stupeco_session", data.session_token);
          }
          await checkAuth();
          window.history.replaceState(null, "", "/app");
          navigate("/app", { replace: true });
          return;
        }

        // -- Legacy Emergent proxy: #session_id=… ---------------------------
        const hash = window.location.hash || "";
        const match = hash.match(/session_id=([^&]+)/);
        const sessionId = match ? decodeURIComponent(match[1]) : null;
        if (sessionId && exchangeGoogleSession) {
          await exchangeGoogleSession(sessionId);
          window.history.replaceState(null, "", window.location.pathname);
          navigate("/app", { replace: true });
          return;
        }

        // -- Nothing to do — bounce to login --------------------------------
        navigate("/login", { replace: true });
      } catch (e) {
        setError(
          formatApiErrorDetail(e?.response?.data?.detail) ||
          "Google sign-in failed. Please try again."
        );
        setTimeout(() => navigate("/login", { replace: true }), 1800);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4" data-testid="auth-callback">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-primary" />
      <p className="text-sm text-muted-foreground">{error || "Signing you in with Google…"}</p>
    </div>
  );
}
